import { useMemo } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import useMachinesOverview from '../hooks/useMachinesOverview';
import MachineStatusOption from './MachineStatusOption';
import styles from './HubSelect.module.scss';

const EMPTY_LIST = [];

// Searchable hub picker built on the machines overview: typing matches
// nickname, serial number and company name. With `allowCustom` the user can
// also enter a serial number that is not in the registry.
function HubSelect({
  value,
  onChange,
  // When given, only these serial numbers are offered (enriched with registry
  // data when available). Otherwise every registered machine is offered.
  serials = null,
  // Extra serial numbers to offer alongside the registered machines.
  extraSerials = EMPTY_LIST,
  allowCustom = false,
  isClearable = true,
  isDisabled = false,
  inputId,
  placeholder = 'Search by nickname, serial or company…',
  noOptionsMessage = 'No hubs found',
}) {
  const overview = useMachinesOverview();

  const machineBySerial = useMemo(() => {
    const map = new Map();
    (overview?.machines ?? []).forEach(machine => {
      map.set(machine.serial_number, {
        nickname: machine.nickname || machine.serial_number,
        serialNumber: machine.serial_number,
        companyName: machine.company_name || '',
        isOnline: Boolean(machine.is_online),
        inOperation: Boolean(machine.in_operation),
      });
    });
    return map;
  }, [overview]);

  const options = useMemo(() => {
    const wanted = serials ?? [...machineBySerial.keys(), ...extraSerials];
    const seen = new Set();
    const list = [];
    wanted.forEach(serial => {
      if (!serial || seen.has(serial)) {
        return;
      }
      seen.add(serial);
      const machine = machineBySerial.get(serial) || null;
      list.push({
        value: serial,
        label: machine ? `${machine.nickname} · ${serial}` : serial,
        machine,
        searchText: [machine?.nickname, serial, machine?.companyName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
      });
    });
    list.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    return list;
  }, [serials, extraSerials, machineBySerial]);

  // A value outside the option list (a custom serial, or one the registry has
  // not loaded yet) still has to render in the control.
  const selectedOption = useMemo(() => {
    if (!value) {
      return null;
    }
    return (
      options.find(option => option.value === value) || {
        value,
        label: value,
        machine: machineBySerial.get(value) || null,
        searchText: value.toLowerCase(),
      }
    );
  }, [value, options, machineBySerial]);

  const formatOptionLabel = (option, { context }) => {
    if (option.__isNew__) {
      return <span className={styles.createOption}>Use custom serial “{option.value}”</span>;
    }
    if (option.machine) {
      return <MachineStatusOption machine={option.machine} compact={context === 'value'} />;
    }
    return (
      <div className={styles.plainOption}>
        <span className={styles.unknownDot} aria-hidden="true" />
        <span className={styles.plainIdentity}>
          <strong>{option.value}</strong>
          {context !== 'value' && <small>Not in the machine registry</small>}
        </span>
      </div>
    );
  };

  const SelectComponent = allowCustom ? CreatableSelect : Select;
  const creatableProps = allowCustom
    ? {
        createOptionPosition: 'first',
        formatCreateLabel: input => `Use custom serial “${input.trim()}”`,
        isValidNewOption: input =>
          Boolean(input.trim()) && !options.some(option => option.value === input.trim()),
        getNewOptionData: (input, label) => ({
          value: input.trim(),
          label,
          machine: null,
          searchText: '',
          __isNew__: true,
        }),
      }
    : {};

  return (
    <SelectComponent
      inputId={inputId}
      classNamePrefix="hubSelect"
      className={styles.select}
      options={options}
      value={selectedOption}
      onChange={option => onChange?.(option ? option.value : '')}
      formatOptionLabel={formatOptionLabel}
      filterOption={(option, input) => {
        if (option.data.__isNew__) {
          return true;
        }
        const query = input.trim().toLowerCase();
        return !query || option.data.searchText.includes(query);
      }}
      isSearchable
      isClearable={isClearable}
      isDisabled={isDisabled}
      placeholder={placeholder}
      noOptionsMessage={() => noOptionsMessage}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPlacement="auto"
      maxMenuHeight={360}
      {...creatableProps}
    />
  );
}

export default HubSelect;
